/* eslint-disable ember/no-observers */
import { tracked } from "@glimmer/tracking";
import EmberObject, { action, computed } from "@ember/object";
import { alias, and, or, reads } from "@ember/object/computed";
import { getOwner } from "@ember/owner";
import { cancel, next, scheduleOnce } from "@ember/runloop";
import Service, { service } from "@ember/service";
import { isEmpty } from "@ember/utils";
import { observes } from "@ember-decorators/object";
import { Promise } from "rsvp";
import DiscardDraftModal from "discourse/components/modal/discard-draft";
import PostEnqueuedModal from "discourse/components/modal/post-enqueued";
import SpreadsheetEditor from "discourse/components/modal/spreadsheet-editor";
import TopicReplyChoiceDialog from "discourse/components/topic-reply-choice-dialog";
import WrapAttributesModal from "discourse/components/wrap-attributes-modal";
import {
  cannotPostAgain,
  durationTextFromSeconds,
} from "discourse/helpers/slow-mode";
import { popupAjaxError } from "discourse/lib/ajax-error";
import { customPopupMenuOptions } from "discourse/lib/composer/custom-popup-menu-options";
import discourseDebounce from "discourse/lib/debounce";
import deprecated from "discourse/lib/deprecated";
import { isRailsTesting } from "discourse/lib/environment";
import prepareFormTemplateData, {
  getFormTemplateObject,
} from "discourse/lib/form-template-validation";
import { shortDate } from "discourse/lib/formatter";
import getURL from "discourse/lib/get-url";
import { disableImplicitInjections } from "discourse/lib/implicit-injections";
import { wantsNewWindow } from "discourse/lib/intercept-click";
import { buildQuote } from "discourse/lib/quote";
import { emojiUnescape } from "discourse/lib/text";
import { applyValueTransformer } from "discourse/lib/transformer";
import {
  authorizesOneOrMoreExtensions,
  uploadIcon,
} from "discourse/lib/uploads";
import DiscourseURL from "discourse/lib/url";
import { escapeExpression } from "discourse/lib/utilities";
import { parseAttributesString } from "discourse/lib/wrap-utils";
import Category from "discourse/models/category";
import Composer, {
  CREATE_TOPIC,
  NEW_PRIVATE_MESSAGE_KEY,
  NEW_TOPIC_KEY,
  SAVE_ICONS,
  SAVE_LABELS,
} from "discourse/models/composer";
import Draft from "discourse/models/draft";
import PostLocalization from "discourse/models/post-localization";
import TopicLocalization from "discourse/models/topic-localization";
import { i18n } from "discourse-i18n";

async function loadDraft(store, opts = {}) {
  let { draft, draftKey, draftSequence } = opts;

  try {
    if (draft && typeof draft === "string") {
      draft = JSON.parse(draft);
    }
  } catch {
    draft = null;
    Draft.clear(draftKey, draftSequence);
  }

  let attrs = {
    draftKey,
    draftSequence,
    draft: true,
    composerState: Composer.DRAFT,
    topic: opts.topic,
  };

  Composer.serializedFieldsForDraft().forEach((f) => {
    attrs[f] = draft[f] || opts[f];
  });

  const composer = store.createRecord("composer");
  await composer.open(attrs);

  return composer;
}

const _composerSaveErrorCallbacks = [];

export function clearComposerSaveErrorCallback() {
  _composerSaveErrorCallbacks.length = 0;
}

export function addComposerSaveErrorCallback(callback) {
  _composerSaveErrorCallbacks.push(callback);
}

@disableImplicitInjections
export default class ComposerService extends Service {
  @service appEvents;
  @service capabilities;
  @service currentUser;
  @service dialog;
  @service keyValueStore;
  @service messageBus;
  @service modal;
  @service router;
  @service site;
  @service siteSettings;
  @service store;
  @service toasts;

  @tracked allowPreview = false;
  @tracked selectedTranslationLocale = null;
  checkedMessages = false;
  messageCount = null;
  showEditReason = false;
  editReason = null;
  scopedCategoryId = null;
  prioritizedCategoryId = null;
  readOnlyCategoryId = null;
  lastValidatedAt = null;
  isUploading = false;
  isProcessingUpload = false;
  isCancellable;
  uploadProgress;
  topic = null;
  linkLookup = null;

  composerHeight = null;

  @and("site.mobileView", "showPreview") forcePreview;
  @alias("site.categoriesList") categories;
  @alias("topicController.model") topicModel;
  @reads("currentUser.staff") isStaffUser;
  @reads("currentUser.whisperer") whisperer;
  @and("model.creatingTopic", "isStaffUser") canUnlistTopic;
  @or("replyingToWhisper", "model.whisper") isWhispering;

  @tracked _showPreview;

  get showPreview() {
    return (
      this._showPreview ??
      (this.site.mobileView
        ? false
        : (this.keyValueStore.get("composer.showPreview") || "true") === "true")
    );
  }

  set showPreview(value) {
    this._showPreview = value;
  }

  /**
   * @returns {import("discourse/controllers/topic").default};
   */
  get topicController() {
    return getOwner(this).lookup("controller:topic");
  }

  get isPreviewVisible() {
    return this.showPreview && this.allowPreview;
  }

  @computed("model.action", "model.post.can_localize_post")
  get showTranslationSelector() {
    return (
      this.model?.get("action") === "add_translation" &&
      this.model?.get("post.can_localize_post")
    );
  }

  @observes("showPreview", "allowPreview")
  previewVisibilityChanged() {
    this.appEvents.trigger("composer:preview-toggled", this.isPreviewVisible);
  }

  get isOpen() {
    return this.model?.composeState === Composer.OPEN;
  }

  get topicDraftKey() {
    return NEW_TOPIC_KEY + "_" + new Date().getTime();
  }

  get privateMessageDraftKey() {
    return NEW_PRIVATE_MESSAGE_KEY + "_" + new Date().getTime();
  }

  @computed(
    "model.loading",
    "isUploading",
    "isProcessingUpload",
    "_disableSubmit"
  )
  get disableSubmit() {
    return (
      this.model?.loading ||
      this.isUploading ||
      this.isProcessingUpload ||
      this._disableSubmit
    );
  }

  set disableSubmit(value) {
    this.set("_disableSubmit", value);
  }

  @computed("model.category", "skipFormTemplate")
  get formTemplateIds() {
    if (!this.siteSettings.enable_form_templates || this.skipFormTemplate) {
      return null;
    }

    return this.model?.category?.get("form_template_ids");
  }

  get hasFormTemplate() {
    return (
      this.formTemplateIds?.length > 0 &&
      !this.get("model.replyingToTopic") &&
      !this.get("model.editingPost")
    );
  }

  get replyingToUserId() {
    if (this.get("model.editingPost")) {
      const user = this.get("model.post.reply_to_user");
      if (user) {
        return user.id;
      }
    }

    const user = this.get("model.post.user");
    if (user) {
      return user.id;
    }

    return this.get("model.topic.user_id");
  }

  get formTemplateInitialValues() {
    return this._formTemplateInitialValues;
  }

  set formTemplateInitialValues(values) {
    this.set("_formTemplateInitialValues", values);
  }

  @action
  onSelectFormTemplate(formTemplate) {
    this.selectedFormTemplate = formTemplate;
  }

  @computed("showPreview")
  get toggleText() {
    return this.showPreview
      ? i18n("composer.hide_preview")
      : i18n("composer.show_preview");
  }

  @observes("showPreview")
  showPreviewChanged() {
    if (this.site.desktopView) {
      this.keyValueStore.set({
        key: "composer.showPreview",
        value: this.showPreview,
      });
    }
  }

  @computed(
    "model.replyingToTopic",
    "model.creatingPrivateMessage",
    "model.targetRecipients",
    "model.composeState"
  )
  get focusTarget() {
    // Focus on usernames if it's blank or if it's just you
    const usernames = this.model?.targetRecipients || "";
    if (
      (this.model?.creatingPrivateMessage && usernames.length === 0) ||
      usernames === this.currentUser.username
    ) {
      return "usernames";
    }

    if (this.model?.replyingToTopic) {
      return "reply";
    }

    if (this.model?.composeState === Composer.FULLSCREEN) {
      return "editor";
    }

    return "title";
  }

  @computed
  get showToolbar() {
    return (
      this._toolbarEnabled ??
      this.keyValueStore.get("toolbar-enabled") !== "false"
    );
  }

  set showToolbar(val) {
    this._toolbarEnabled = val;
    this.keyValueStore.set({
      key: "toolbar-enabled",
      value: val ? "true" : "false",
    });
  }

  @computed("model.canEditTitle", "model.creatingPrivateMessage")
  get canEditTags() {
    const isPrivateMessage =
      this.model?.creatingPrivateMessage ||
      this.get("model.topic.isPrivateMessage");
    return (
      this.model?.canEditTitle &&
      this.site.can_tag_topics &&
      (!isPrivateMessage || this.site.can_tag_pms)
    );
  }

  @computed("model.editingPost", "model.topic.details.can_edit")
  get disableCategoryChooser() {
    return this.model?.editingPost && !this.model?.topic?.details?.can_edit;
  }

  @computed("model.editingPost", "model.topic.canEditTags")
  get disableTagsChooser() {
    return this.model?.editingPost && !this.model?.topic?.canEditTags;
  }

  @computed("canWhisper", "replyingToWhisper")
  get showWhisperToggle() {
    return this.canWhisper && !this.replyingToWhisper;
  }

  @computed("model.post")
  get replyingToWhisper() {
    return (
      this.model?.post &&
      this.model?.post?.post_type === this.site.post_types.whisper
    );
  }

  @computed("model.action", "isWhispering", "model.privateMessage")
  get saveIcon() {
    if (this.isWhispering) {
      return "far-eye-slash";
    }
    if (this.model?.privateMessage && this.model?.action === Composer.REPLY) {
      return "envelope";
    }

    return SAVE_ICONS[this.model?.action];
  }

  // Note we update when some other attributes like tag/category change to allow
  // text customizations to use those.
  @computed(
    "model.action",
    "isWhispering",
    "model.editConflict",
    "model.privateMessage",
    "model.tags",
    "model.category"
  )
  get saveLabel() {
    let result = this.model.customizationFor("saveLabel");
    if (result) {
      return result;
    }

    if (this.model?.editConflict) {
      return "composer.overwrite_edit";
    } else if (this.isWhispering) {
      return "composer.create_whisper";
    } else if (
      this.model?.privateMessage &&
      this.model?.action === Composer.REPLY
    ) {
      return "composer.create_pm";
    }

    return SAVE_LABELS[this.model?.action];
  }

  @computed("whisperer", "model.action")
  get canWhisper() {
    return this.whisperer && this.model?.action === Composer.REPLY;
  }

  _setupPopupMenuOption(option) {
    // Backwards compatibility support for when we used to accept a function.
    // This can be dropped when `addToolbarPopupMenuOptionsCallback` is removed from `plugin-api.js`.
    if (typeof option === "function") {
      option = option(this);
    }

    if (typeof option === "undefined") {
      return null;
    }

    const conditionType = typeof option.condition;

    if (conditionType === "undefined") {
      option.condition = true;
    } else if (conditionType === "boolean") {
      // uses existing value
    } else if (conditionType === "function") {
      option.condition = option.condition(this);
    } else {
      option.condition = this.get(option.condition);
    }

    return option;
  }

  @computed("model.requiredCategoryMissing", "model.replyLength")
  get disableTextarea() {
    return this.model?.requiredCategoryMissing && this.model?.replyLength === 0;
  }

  @computed("model.composeState", "model.creatingTopic", "model.post")
  get popupMenuOptions() {
    if (
      this.model?.composeState === "open" ||
      this.model?.composeState === "fullscreen"
    ) {
      const options = [];

      options.push(
        this._setupPopupMenuOption({
          name: "quote",
          action: this.importQuote,
          icon: "far-comment",
          label: "composer.quote_post_title",
        })
      );

      if (this.capabilities.touch) {
        options.push(
          this._setupPopupMenuOption({
            name: "format-code",
            action: "applyFormatCode",
            icon: "code",
            label: "composer.code_title",
          })
        );
      }

      options.push(
        this._setupPopupMenuOption({
          name: "toggle-spreadsheet",
          action: "toggleSpreadsheet",
          icon: "table",
          label: "composer.insert_table",
        })
      );

      const secondaryOptions = [
        this._setupPopupMenuOption({
          name: "toggle-wrap",
          action: this.toggleWrap,
          icon: "right-to-bracket",
          label: "composer.apply_wrap_title",
          showActiveIcon: true,
          active: ({ state }) => state?.inWrap,
        }),
      ];

      return [
        ...options,
        ...customPopupMenuOptions
          .filter((option) => !option.menu)
          .map((option) => this._setupPopupMenuOption({ ...option }))
          .filter(Boolean),
        ...secondaryOptions,
      ];
    }
  }

  @computed(
    "model.creatingPrivateMessage",
    "model.targetRecipients",
    "model.warningsDisabled"
  )
  get showWarning() {
    if (!this.get("currentUser.staff") || this.model?.warningsDisabled) {
      return false;
    }

    const hasTargetGroups = this.get("model.hasTargetGroups");

    // We need exactly one user to issue a warning
    if (
      isEmpty(this.model?.targetRecipients) ||
      this.model?.targetRecipients?.split(",")?.length !== 1 ||
      hasTargetGroups
    ) {
      return false;
    }

    return this.model?.creatingPrivateMessage;
  }

  @computed("model.topic.title")
  get draftTitle() {
    return emojiUnescape(escapeExpression(this.model?.topic?.title));
  }

  @computed
  get allowUpload() {
    return authorizesOneOrMoreExtensions(
      this.currentUser.staff,
      this.siteSettings
    );
  }

  @computed()
  get uploadIcon() {
    return uploadIcon(this.currentUser.staff, this.siteSettings);
  }

  @computed(
    "model.action",
    "isWhispering",
    "model.privateMessage",
    "model.post.username"
  )
  get ariaLabel() {
    switch (this.model?.action) {
      case "createSharedDraft":
        return i18n("composer.create_shared_draft");
      case "editSharedDraft":
        return i18n("composer.edit_shared_draft");
      case "createTopic":
        return i18n("composer.composer_actions.create_topic.label");
      case "privateMessage":
        return i18n("user.new_private_message");
      case "edit":
        return i18n("composer.composer_actions.edit");
      case "reply":
        if (this.isWhispering) {
          return `${i18n("composer.create_whisper")} ${this.site.get(
            "whispers_allowed_groups_names"
          )}`;
        }
        if (this.model?.privateMessage) {
          return i18n("composer.create_pm");
        }
        if (this.model?.post?.username) {
          return i18n("composer.composer_actions.reply_to_post.label", {
            postUsername: this.model?.post?.username,
          });
        } else {
          return i18n("composer.composer_actions.reply_to_topic.label");
        }
      default:
        return i18n("keyboard_shortcuts_help.composing.title");
    }
  }

  /**
   * Opens the composer when uncertain if it's already open or has a draft.
   * Supports appending text once the composer is open.
   *
   * @param {Object} opts - Configuration options
   * @param {Object} [opts.topic] - If provided, opens composer with reply action,
   *   using the current topic key and draft sequence
   * @param {boolean} [opts.fallbackToNewTopic] - If true and no draft/topic exists,
   *   opens composer with create_topic action and new topic draft key
   * @param {string} [opts.insertText] - Text to append to the composer after opening
   * @param {Object} [opts.openOpts] - Additional options passed to this.open when
   *   fallbackToNewTopic is true or topic is provided
   * @returns {Promise<void>}
   */
  @action
  async focusComposer(opts = {}) {
    await this._openComposerForFocus(opts);

    scheduleOnce(
      "afterRender",
      this,
      this._focusAndInsertText,
      opts.insertText
    );
  }

  async _openComposerForFocus(opts) {
    if (this.get("model.viewOpen")) {
      return;
    }

    const opened = this.openIfDraft();
    if (opened) {
      return;
    }

    if (opts.topic) {
      return await this.open({
        action: Composer.REPLY,
        draftKey: opts.topic.get("draft_key"),
        draftSequence: opts.topic.get("draft_sequence"),
        topic: opts.topic,
        ...(opts.openOpts || {}),
      });
    }

    if (opts.fallbackToNewTopic) {
      return await this.open({
        action: CREATE_TOPIC,
        draftKey: this.topicDraftKey,
        ...(opts.openOpts || {}),
      });
    }
  }

  _focusAndInsertText(insertText) {
    next(() =>
      document.querySelector(".d-editor-container .d-editor-input")?.focus()
    );

    if (insertText) {
      this.model.appendText(insertText, null, { new_line: true });
    }
  }

  @action
  updateCategory(categoryId) {
    this.model.categoryId = categoryId;
  }

  @action
  openIfDraft(event) {
    if (!this.get("model.viewDraft")) {
      return false;
    }

    // when called from shortcut, ensure we don't propagate the key to
    // the composer input title
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.set("model.composeState", Composer.OPEN);

    document.documentElement.style.setProperty(
      "--composer-height",
      this.get("model.composerHeight")
    );

    return true;
  }

  @action
  removeFullScreenExitPrompt() {
    this.set("model.showFullScreenExitPrompt", false);
  }

  @action
  async saveAndClose(event) {
    event?.preventDefault();
    await this.saveAndCloseComposer();
  }

  @action
  async cancel(event) {
    event?.preventDefault();
    await this.cancelComposer();
  }

  @action
  cancelUpload(event) {
    event?.preventDefault();
    this.appEvents.trigger("composer:cancel-upload");
  }

  @action
  togglePreview(event) {
    event?.preventDefault();
    this.toggleProperty("showPreview");
  }

  @action
  viewNewReply(event) {
    if (wantsNewWindow(event)) {
      return;
    }

    event.preventDefault();
    DiscourseURL.routeTo(this.get("model.createdPost.url"));
    this.close();
  }

  @action
  closeComposer() {
    this.close();
  }

  @action
  onPopupMenuAction(menuItem, toolbarEvent) {
    // menuItem is an object with keys name & action like so: { name: "toggle-invisible, action: "toggleInvisible" }
    // `action` value can either be a string (to lookup action by) or a function to call
    this.appEvents.trigger(
      "composer:toolbar-popup-menu-button-clicked",
      menuItem
    );
    if (typeof menuItem.action === "function") {
      // toolbarEvent is passed when triggered via keyboard shortcut,
      // otherwise fall back to stored toolbarEvent from menu open
      return menuItem.action(toolbarEvent ?? this.toolbarEvent);
    } else {
      return (
        this.actions?.[menuItem.action]?.bind(this) || // Legacy-style contributions from themes/plugins
        this[menuItem.action]
      )();
    }
  }

  @action
  storeToolbarState(toolbarEvent) {
    this.set("toolbarEvent", toolbarEvent);
  }

  @action
  typed() {
    this.checkReplyLength();
    this.model.typing();
  }

  @action
  cancelled() {
    this.hitEsc();
  }

  @action
  addLinkLookup(linkLookup) {
    this.set("linkLookup", linkLookup);
  }

  @action
  afterRefresh(preview) {
    const topic = this.get("model.topic");
    const linkLookup = this.linkLookup;

    if (!topic || !linkLookup) {
      return;
    }

    // Don't check if there's only one post
    if (topic.posts_count === 1) {
      return;
    }

    const post = this.get("model.post");
    preview.querySelectorAll("a[href]").forEach((l) => {
      const href = l.href;
      if (href && href.length) {
        // skip links added by watched words
        if (l.dataset.word !== undefined) {
          return;
        }

        // skip links in quotes and oneboxes
        for (let element = l; element; element = element.parentElement) {
          if (
            element.tagName === "DIV" &&
            element.classList.contains("d-editor-preview")
          ) {
            break;
          }

          if (
            element.tagName === "ASIDE" &&
            element.classList.contains("quote")
          ) {
            return;
          }

          if (
            element.tagName === "ASIDE" &&
            element.classList.contains("onebox") &&
            href !== element.dataset["onebox-src"]
          ) {
            return;
          }
        }

        const [linkWarn, linkInfo] = linkLookup.check(post, href);

        if (linkWarn && !this.isWhispering) {
          if (linkInfo.username === this.currentUser.username) {
            this.appEvents.trigger("composer-messages:create", {
              extraClass: "custom-body",
              templateName: "education",
              body: i18n("composer.duplicate_link_same_user", {
                domain: linkInfo.domain,
                post_url: topic.urlForPostNumber(linkInfo.post_number),
                ago: shortDate(linkInfo.posted_at),
              }),
            });
          } else {
            this.appEvents.trigger("composer-messages:create", {
              extraClass: "custom-body duplicate-link-message",
              templateName: "education",
              body: i18n("composer.duplicate_link", {
                domain: linkInfo.domain,
                username: linkInfo.username,
                post_url: topic.urlForPostNumber(linkInfo.post_number),
                ago: shortDate(linkInfo.posted_at),
              }),
            });
          }
        }
      }
    });
  }

  @action
  toggleWhisper() {
    this.toggleProperty("model.whisper");
  }

  @action
  toggleSpreadsheet() {
    this.modal.show(SpreadsheetEditor, {
      model: {
        toolbarEvent: this.toolbarEvent,
        tableTokens: null,
      },
    });
  }

  @action
  toggleWrap(toolbarEvent) {
    const initialAttributes = toolbarEvent.state?.inWrap
      ? toolbarEvent.state.wrapAttributes || ""
      : "";

    this.modal.show(WrapAttributesModal, {
      model: {
        initialAttributes,
        onApply: (attributesString) => {
          if (toolbarEvent.state?.inWrap) {
            toolbarEvent.commands?.updateWrap(attributesString);
          } else if (toolbarEvent.commands?.insertWrap) {
            toolbarEvent.commands.insertWrap(
              parseAttributesString(attributesString)
            );
          } else {
            const wrapTag = attributesString.trim()
              ? `[wrap${attributesString}]`
              : "[wrap]";
            toolbarEvent.applySurround(
              `${wrapTag}\n`,
              "\n[/wrap]",
              "wrap_text"
            );
          }
        },
        onRemove: toolbarEvent.state?.inWrap
          ? () => toolbarEvent.commands?.removeWrap()
          : undefined,
      },
    });
  }

  @action
  toggleToolbar() {
    this.toggleProperty("showToolbar");
  }

  // Toggle the reply view
  @action
  async toggle() {
    const composer = this.model;

    if (composer?.viewOpenOrFullscreen) {
      this.shrink();
    } else {
      await this.cancelComposer();
    }
  }

  @action
  fullscreenComposer() {
    this.toggleFullscreen();
    return false;
  }

  // Import a quote from the post
  @action
  async importQuote(toolbarEvent) {
    const postStream = this.get("topic.postStream");
    let postId = this.get("model.post.id");

    // If there is no current post, use the first post id from the stream
    if (!postId && postStream) {
      postId = postStream.firstPostId;
    }

    // If we're editing a post, fetch the reply when importing a quote
    if (this.get("model.editingPost")) {
      const replyToPostNumber = this.get("model.post.reply_to_post_number");
      if (replyToPostNumber) {
        const replyPost = postStream.posts.find(
          (item) => item.post_number === replyToPostNumber
        );

        if (replyPost) {
          postId = replyPost.id;
        }
      }
    }

    if (!postId) {
      return;
    }

    this.set("model.loading", true);

    const post = await this.store.find("post", postId);
    const quote = buildQuote(post, post.raw, { full: true });

    toolbarEvent.addText(quote);
    this.set("model.loading", false);
  }

  @action
  saveAction(ignore, event) {
    this.save(false, {
      jump:
        !(event?.shiftKey && this.get("model.replyingToTopic")) &&
        !this.skipJumpOnSave,
    });
  }

  @action
  displayEditReason() {
    this.set("showEditReason", true);
  }

  @action
  hitEsc() {
    if (document.querySelectorAll(".emoji-picker-modal.fadeIn").length === 1) {
      this.appEvents.trigger("emoji-picker:close");
      return;
    }

    if ((this.messageCount || 0) > 0) {
      this.appEvents.trigger("composer-messages:close");
      return;
    }

    const composer = this.model;

    if (composer?.viewOpen) {
      this.shrink();
    }

    if (composer?.viewFullscreen) {
      this.toggleFullscreen();
      this.focusComposer();
    }
  }

  @action
  groupsMentioned({ name, userCount, maxMentions }) {
    if (
      this.get("model.creatingPrivateMessage") ||
      this.get("model.topic.isPrivateMessage")
    ) {
      return;
    }

    maxMentions = parseInt(maxMentions, 10);
    userCount = parseInt(userCount, 10);

    let body;
    const groupLink = getURL(`/g/${name}/members`);

    if (userCount > maxMentions) {
      body = i18n("composer.group_mentioned_limit", {
        group: `@${name}`,
        count: maxMentions,
        group_link: groupLink,
      });
    } else if (userCount > 0) {
      // Louder warning for a larger group.
      const translationKey =
        userCount >= 5
          ? "composer.larger_group_mentioned"
          : "composer.group_mentioned";
      body = i18n(translationKey, {
        group: `@${name}`,
        count: userCount,
        group_link: groupLink,
      });
    }

    if (body) {
      this.appEvents.trigger("composer-messages:create", {
        extraClass: "custom-body",
        templateName: "education",
        body,
      });
    }
  }

  @action
  cannotSeeMention({ name, reason, notifiedCount, isGroup }) {
    notifiedCount = parseInt(notifiedCount, 10);

    let body;
    if (isGroup) {
      body = i18n(`composer.cannot_see_group_mention.${reason}`, {
        group: name,
        count: notifiedCount,
      });
    } else {
      body = i18n(`composer.cannot_see_mention.${reason}`, {
        username: name,
      });
    }

    this.appEvents.trigger("composer-messages:create", {
      extraClass: "custom-body",
      templateName: "education",
      body,
    });
  }

  @action
  hereMention(count) {
    this.appEvents.trigger("composer-messages:create", {
      extraClass: "custom-body",
      templateName: "education",
      body: i18n("composer.here_mention", {
        here: this.siteSettings.here_mention,
        count,
      }),
    });
  }

  @action
  applyFormatCode() {
    this.toolbarEvent.formatCode();
  }

  save(force, options = {}) {
    if (this.disableSubmit) {
      return;
    }

    if (this.model.action === Composer.ADD_TRANSLATION) {
      return this.saveTranslation();
    }

    // Clear the warning state if we're not showing the checkbox anymore
    if (!this.showWarning) {
      this.set("model.isWarning", false);
    }

    if (this.site.mobileView && this.showPreview) {
      this.set("showPreview", false);
    }

    if (this.hasFormTemplate) {
      const formTemplateData = prepareFormTemplateData(
        document.querySelector("#form-template-form"),
        this.selectedFormTemplate
      );
      if (formTemplateData) {
        this.model.set("reply", formTemplateData);
      } else {
        return;
      }
    }

    const composer = this.model;
    const cantSubmitPost = applyValueTransformer(
      "composer-service-cannot-submit-post",
      composer?.cantSubmitPost,
      { model: composer }
    );

    if (cantSubmitPost) {
      if (composer?.viewFullscreen) {
        this.toggleFullscreen();
      }

      this.set("lastValidatedAt", Date.now());
      this.appEvents.trigger("composer-service:last-validated-at-updated", {
        model: composer,
      });
      return;
    }

    const topic = composer.topic;
    const slowModePost =
      topic && topic.slow_mode_seconds && topic.user_last_posted_at;
    const notEditing = this.get("model.action") !== "edit";

    // Editing a topic in slow mode is directly handled by the backend.
    if (slowModePost && notEditing) {
      if (
        cannotPostAgain(
          this.currentUser,
          topic.slow_mode_seconds,
          topic.user_last_posted_at
        )
      ) {
        const canPostAt = new moment(topic.user_last_posted_at).add(
          topic.slow_mode_seconds,
          "seconds"
        );
        const timeLeft = moment().diff(canPostAt, "seconds");
        const message = i18n("composer.slow_mode.error", {
          timeLeft: durationTextFromSeconds(timeLeft),
        });

        this.dialog.alert(message);
        return;
      } else {
        // Edge case where the user tries to post again immediately.
        topic.set("user_last_posted_at", new Date().toISOString());
      }
    }

    composer.set("disableDrafts", true);

    // for now handle a very narrow use case
    // if we are replying to a topic
    // AND are on a different topic
    // AND topic is open (or we are staff)
    // --> pop the window up
    if (!force && composer.replyingToTopic) {
      const currentTopic = this.topicModel;
      const originalTopic = this.model.topic;

      if (!currentTopic) {
        this.save(true);
        return;
      }

      if (
        currentTopic.id !== composer.get("topic.id") &&
        (this.isStaffUser || !currentTopic.closed)
      ) {
        this.dialog.alert({
          title: i18n("composer.posting_not_on_topic"),
          bodyComponent: TopicReplyChoiceDialog,
          bodyComponentModel: {
            originalTopic,
            replyOnOriginal: () => {
              this.save(true);
              this.dialog.didConfirmWrapped();
            },
            currentTopic,
            replyOnCurrent: () => {
              composer.setProperties({ topic: currentTopic, post: null });
              this.save(true);
              this.dialog.didConfirmWrapped();
            },
          },
          buttons: [
            {
              label: i18n("composer.cancel"),
              class: "btn-flat btn-text btn-reply-where__cancel",
            },
          ],
          class: "reply-where-modal",
          didCancel: () => {
            composer.set("disableDrafts", false);
            this.skipAutoSave = true;
            this._saveDraft(true);
            this.skipAutoSave = false;
          },
        });
        return;
      }
    }

    let staged = false;

    // TODO: This should not happen in model
    const imageSizes = {};
    document
      .querySelectorAll(
        "#reply-control .d-editor-preview img:not(.avatar, .emoji)"
      )
      .forEach((e) => {
        const src = e.src;

        if (src && src.length) {
          imageSizes[src] = { width: e.naturalWidth, height: e.naturalHeight };
        }
      });

    const promise = composer
      .save({ imageSizes, editReason: this.editReason })
      .then((result) => {
        this.appEvents.trigger("composer:saved");

        if (result.responseJson.action === "enqueued") {
          this.postWasEnqueued(result.responseJson);
          if (result.responseJson.pending_post) {
            let pendingPosts = this.topicController.model?.pending_posts;
            if (pendingPosts) {
              pendingPosts.push(result.responseJson.pending_post);
            }
          }

          return this.destroyDraft().then(() => {
            this.close();
            return result;
          });
        }

        if (this.get("model.editingPost")) {
          this.appEvents.trigger("composer:edited-post");
          if (result.responseJson.post.post_number === 1) {
            this.appEvents.trigger("header:update-topic", composer.topic);
          }
        }

        if (result.responseJson.action === "create_post") {
          this.appEvents.trigger("composer:created-post", {
            postId: result.responseJson.post.id,
          });
          this.appEvents.trigger(
            "post:highlight",
            result.payload.post_number,
            options
          );
        }

        if (result.responseJson.route_to) {
          // TODO: await this:
          this.destroyDraft();
          if (result.responseJson.message) {
            return this.dialog.alert({
              message: result.responseJson.message,
              didConfirm: () => {
                DiscourseURL.routeTo(result.responseJson.route_to);
              },
            });
          }
          return DiscourseURL.routeTo(result.responseJson.route_to);
        }

        this.close();

        this.currentUser.set("any_posts", true);

        const post = result.target;

        if (post && !staged && options.jump !== false) {
          DiscourseURL.routeTo(post.url, {
            keepFilter: true,
            skipIfOnScreen: true,
          });
        }
      })
      .catch((error) => {
        composer.set("disableDrafts", false);
        if (error) {
          this.appEvents.one("composer:will-open", () => {
            if (
              _composerSaveErrorCallbacks.length === 0 ||
              !_composerSaveErrorCallbacks
                .map((c) => {
                  return c.call(this, error);
                })
                .some((i) => {
                  return i;
                })
            ) {
              this.dialog.alert(error);
            }
          });
        }
      });

    if (
      this.router.currentRouteName.split(".")[0] === "topic" &&
      composer.get("topic.id") === this.get("topicModel.id")
    ) {
      staged = composer.get("stagedPost");
    }

    this.messageBus.pause();
    promise.finally(() => this.messageBus.resume());

    return promise;
  }

  async saveTranslation() {
    this.set("model.loading", true);

    this.set("lastValidatedAt", Date.now());
    this.appEvents.trigger("composer-service:last-validated-at-updated", {
      model: this.model,
    });

    try {
      await PostLocalization.createOrUpdate(
        this.model.post.id,
        this.selectedTranslationLocale,
        this.model.reply
      );

      if (this.model.post.firstPost) {
        await TopicLocalization.createOrUpdate(
          this.model.post.topic_id,
          this.selectedTranslationLocale,
          this.model.title
        );
      }

      this.set("model.loading", false);
      this.close();
      this.toasts.success({
        duration: "short",
        data: {
          message: i18n("post.localizations.success"),
        },
      });
      this.selectedTranslationLocale = null;
    } catch (e) {
      popupAjaxError(e);
    }
  }

  @action
  postWasEnqueued(details) {
    this.modal.show(PostEnqueuedModal, { model: details });
  }

  // Notify the composer messages controller that a reply has been typed. Some
  // messages only appear after typing.
  checkReplyLength() {
    if (!isEmpty("model.reply")) {
      this.appEvents.trigger("composer:typed-reply");
    }
  }

  /**
   Open the composer view

   @method open
   @param {Object} opts Options for creating a post
   @param {String} opts.action The action we're performing: edit, reply, createTopic, createSharedDraft, privateMessage, addTranslation
   @param {String} opts.draftKey
   @param {Post} [opts.post] The post we're replying to
   @param {Topic} [opts.topic] The topic we're replying to
   @param {String} [opts.quote] If we're opening a reply from a quote, the quote we're making
   @param {Boolean} [opts.ignoreIfChanged]
   @param {Boolean} [opts.disableScopedCategory]
   @param {Number} [opts.categoryId] Sets `scopedCategoryId` and `categoryId` on the Composer model
   @param {Number} [opts.prioritizedCategoryId]
   @param {Number} [opts.readOnlyCategoryId] Shows category as read-only in category chooser, with a read-only badge
   @param {Number} [opts.formTemplateId]
   @param {String} [opts.draftSequence]
   @param {Boolean} [opts.skipJumpOnSave] Option to skip navigating to the post when saved in this composer session
   @param {Boolean} [opts.skipFormTemplate] Option to skip the form template even if configured for the category
   @param {String} [opts.hijackPreview] Option to hijack the preview with a custom component, you must pass { component: CustomPreviewComponent, model: { ... } }
   @param {String} [opts.selectedTranslationLocale] The locale to use for the translation
   **/
  async open(opts = {}) {
    if (!opts.draftKey) {
      throw new Error("composer opened without a proper draft key");
    }

    let composerModel = this.model;

    if (
      opts.ignoreIfChanged &&
      composerModel &&
      composerModel.composeState !== Composer.CLOSED
    ) {
      return;
    }

    this.setProperties({
      showEditReason: false,
      editReason: null,
      scopedCategoryId: null,
      prioritizedCategoryId: null,
      readOnlyCategoryId: null,
      skipAutoSave: true,
    });

    this.set("skipJumpOnSave", !!opts.skipJumpOnSave);

    this.set("skipFormTemplate", !!opts.skipFormTemplate);

    if (opts.hijackPreview) {
      this.set("hijackPreview", opts.hijackPreview);
    }

    this.selectedTranslationLocale = opts.selectedTranslationLocale ?? null;

    // Scope the categories drop down to the category we opened the composer with.
    if (opts.categoryId && !opts.disableScopedCategory) {
      const category = Category.findById(opts.categoryId);
      if (category) {
        this.set("scopedCategoryId", opts.categoryId);
      }
    }

    if (opts.prioritizedCategoryId) {
      const category = Category.findById(opts.prioritizedCategoryId);

      if (category) {
        this.set("prioritizedCategoryId", opts.prioritizedCategoryId);
      }
    }

    if (opts.readOnlyCategoryId) {
      this.set("readOnlyCategoryId", opts.readOnlyCategoryId);
    }

    // If we want a different draft than the current composer, close it and clear our model.
    if (
      composerModel &&
      opts.draftKey !== composerModel.draftKey &&
      composerModel.composeState === Composer.DRAFT
    ) {
      // Check if content is dirty before auto-closing
      if (composerModel.anyDirty) {
        const retry = await this.cancelComposer(opts);
        if (retry) {
          await this.open(opts);
        }
        return;
      }
      this.close();
      composerModel = null;
    }

    try {
      if (composerModel?.replyDirty) {
        // If we're already open, we don't have to do anything
        if (
          composerModel.composeState === Composer.OPEN &&
          composerModel.draftKey === opts.draftKey &&
          !opts.action
        ) {
          return;
        }

        // If it's the same draft, just open it up again.
        if (
          composerModel.composeState === Composer.DRAFT &&
          composerModel.draftKey === opts.draftKey
        ) {
          composerModel.set("composeState", Composer.OPEN);

          // reset height set from collapse() state
          document.documentElement.style.setProperty(
            "--composer-height",
            this.get("model.composerHeight")
          );

          if (!opts.action) {
            return;
          }
        }

        const retry = await this.cancelComposer(opts);
        if (retry) {
          await this.open(opts);
        }
        return;
      }

      if (composerModel && composerModel.action !== opts.action) {
        composerModel.setProperties({ unlistTopic: false, whisper: false });
      }

      await this._setModel(composerModel, opts);
    } finally {
      this.skipAutoSave = false;
      this.appEvents.trigger("composer:open", { model: this.model });
    }
  }

  @action
  async openNewTopic({ title, body, category, tags, formTemplate } = {}) {
    const readOnlyCategoryId = !category?.canCreateTopic ? category?.id : null;
    tags = await this.filterTags(tags);

    return this.open({
      prioritizedCategoryId: category?.id,
      topicCategoryId: category?.id,
      formTemplateId: formTemplate?.id,
      topicTitle: title,
      topicBody: body,
      topicTags: tags,
      action: CREATE_TOPIC,
      draftKey: this.topicDraftKey,
      draftSequence: 0,
      locale: null,
      readOnlyCategoryId,
    });
  }

  @action
  async openNewMessage({ title, body, recipients, hasGroups, tags }) {
    tags = await this.filterTags(tags);
    return this.open({
      action: Composer.PRIVATE_MESSAGE,
      recipients,
      topicTitle: title,
      topicBody: body,
      topicTags: tags,
      archetypeId: "private_message",
      draftKey: this.privateMessageDraftKey,
      hasGroups,
    });
  }

  async filterTags(tags) {
    if (!tags || this.currentUser?.staff) {
      return tags;
    }

    if (typeof tags === "string") {
      tags = await this.store.findAll("listTag", {
        only_tags: tags.split(","),
      });
    }

    return tags.content
      .filter((t) => !t.staff)
      .map((t) => t.name)
      .join(",");
  }

  // Given a potential instance and options, set the model for this composer.
  async _setModel(optionalComposerModel, opts) {
    this.set("linkLookup", null);

    let composerModel;
    if (opts.draft) {
      composerModel = await loadDraft(this.store, opts);

      if (!composerModel) {
        throw new Error("draft was not found");
      }
    } else {
      const model =
        optionalComposerModel || this.store.createRecord("composer");

      await model.open(opts);
      composerModel = model;
    }

    this.set("model", composerModel);

    composerModel.setProperties({
      composeState: Composer.OPEN,
      isWarning: false,
      hasTargetGroups: opts.hasGroups,
      warningsDisabled: opts.warningsDisabled,
      locale: this._initialLocale(opts),
    });

    if (!this.model.targetRecipients) {
      if (opts.usernames) {
        deprecated("`usernames` is deprecated, use `recipients` instead.", {
          id: "discourse.composer.usernames",
        });
        this.model.set("targetRecipients", opts.usernames);
      } else if (opts.recipients) {
        this.model.set("targetRecipients", opts.recipients);
      }
    }

    if (
      opts.topicTitle &&
      opts.topicTitle.length <= this.siteSettings.max_topic_title_length
    ) {
      this.model.set("title", opts.topicTitle);
    }

    const isPrivateMessage = opts.action === Composer.PRIVATE_MESSAGE;
    const canTag =
      this.site.can_tag_topics && (!isPrivateMessage || this.site.can_tag_pms);

    if (opts.topicTags && canTag) {
      let tags = escapeExpression(opts.topicTags)
        .split(",")
        .slice(0, this.siteSettings.max_tags_per_topic);

      tags.forEach(
        (tag, index, array) =>
          (array[index] = tag.substring(0, this.siteSettings.max_tag_length))
      );

      this.model.set("tags", tags);
    }

    if (opts.topicBody) {
      this.model.set("reply", opts.topicBody);
    }

    if (
      opts.formTemplateId &&
      this.model
        .get("category.form_template_ids")
        ?.includes(opts.formTemplateId)
    ) {
      this.model.set("formTemplateId", opts.formTemplateId);
    }

    if (opts.prependText && !this.model.reply?.includes(opts.prependText)) {
      this.model.prependText(opts.prependText, {
        new_line: true,
      });
    }

    const defaultComposerHeight = this._getDefaultComposerHeight();

    this.set("model.composerHeight", defaultComposerHeight);
    document.documentElement.style.setProperty(
      "--composer-height",
      defaultComposerHeight
    );
  }

  _getDefaultComposerHeight() {
    if (this.keyValueStore.getItem("composerHeight")) {
      return this.keyValueStore.getItem("composerHeight");
    }

    // The two custom properties below can be overridden by themes/plugins to set different default composer heights.
    if (this.model.action === "reply") {
      return "var(--reply-composer-height, 255px)";
    } else {
      return "var(--new-topic-composer-height, 400px)";
    }
  }

  async destroyDraft(draftSequence = null) {
    const key = this.get("model.draftKey");
    if (!key) {
      return;
    }

    if (this._saveDraftPromise) {
      await this._saveDraftPromise;
      return await this.destroyDraft();
    }

    const sequence = draftSequence || this.get("model.draftSequence");
    await Draft.clear(key, sequence);
    this.appEvents.trigger("draft:destroyed", key);
  }

  cancelComposer() {
    this.skipAutoSave = true;

    cancel(this._saveDraftDebounce);

    return new Promise((resolve) => {
      if (this.get("model.anyDirty")) {
        this.modal.show(DiscardDraftModal, {
          model: {
            onDestroyDraft: () => {
              return this.destroyDraft()
                .then(() => {
                  this.model.clearState();
                  this.close();
                })
                .finally(() => {
                  this.appEvents.trigger("composer:cancelled");
                  resolve(true);
                });
            },
            onCancelDiscard: () => resolve(false),
          },
        });
      } else {
        // it is possible there is some sort of crazy draft with no body ... just give up on it
        this.destroyDraft()
          .then(() => {
            this.model.clearState();
            this.close();
          })
          .finally(() => {
            this.appEvents.trigger("composer:cancelled");
            resolve();
          });
      }
    }).finally(() => {
      this.skipAutoSave = false;
    });
  }

  saveAndCloseComposer() {
    // Always save the draft if the user had typed something
    // or had started setting up a title/tags/category
    if (this.model.anyDirty) {
      this.skipAutoSave = true;
      this._saveDraft(true);
      this.model.clearState();
      this.close();
      this.appEvents.trigger("composer:cancelled");
      this.skipAutoSave = false;
      return true;
    } else {
      // Otherwise just close the composer and discard any empty draft
      return this.cancelComposer();
    }
  }

  unshrink() {
    this.model.set("composeState", Composer.OPEN);
    document.documentElement.style.setProperty(
      "--composer-height",
      this.model.composerHeight
    );
  }

  shrink() {
    this.collapse();
  }

  _saveDraft(showToast = false) {
    cancel(this._saveDraftDebounce);

    if (!this.model) {
      return;
    }

    if (this.model.draftSaving) {
      this._saveDraftDebounce = discourseDebounce(this, this._saveDraft, 2000);
    } else {
      // This is a temporary solution to avoid losing the current form template state
      // until we have a proper draft system for these forms
      if (this.hasFormTemplate) {
        const form = document.querySelector("#form-template-form");
        if (form) {
          this.set("formTemplateInitialValues", getFormTemplateObject(form));
        }
      }

      this._saveDraftPromise = this.model
        .saveDraft()
        .then(() => {
          if (showToast) {
            this.toasts.success({
              duration: "short",
              data: {
                message: i18n("composer.draft_saved"),
              },
            });
          }
        })
        .finally(() => {
          this._lastDraftSaved = Date.now();
          this._saveDraftPromise = null;
        });
    }
  }

  @observes("model.reply", "model.title")
  _shouldSaveDraft() {
    if (
      this.model &&
      !this.model.loading &&
      !this.skipAutoSave &&
      !this.model.disableDrafts
    ) {
      if (!this._lastDraftSaved) {
        // pretend so we get a save unconditionally in 15 secs
        this._lastDraftSaved = Date.now();
      }

      // Speed this up a bit in system specs so we don't have to wait as long
      if (
        Date.now() - this._lastDraftSaved >
        (isRailsTesting() ? 3000 : 15000)
      ) {
        this._saveDraft();
      } else {
        this._saveDraftDebounce = discourseDebounce(
          this,
          this._saveDraft,
          2000
        );
      }
    }
  }

  @computed("model.categoryId", "lastValidatedAt")
  get categoryValidation() {
    if (
      !this.siteSettings.allow_uncategorized_topics &&
      !this.model?.categoryId
    ) {
      return EmberObject.create({
        failed: true,
        reason: i18n("composer.error.category_missing"),
        lastShownAt: this.lastValidatedAt,
      });
    }
  }

  @computed("model.category", "model.tags", "lastValidatedAt")
  get tagValidation() {
    const tagsArray = this.model?.tags || [];
    if (
      this.site.can_tag_topics &&
      !this.currentUser.staff &&
      this.model?.category
    ) {
      // category.minimumRequiredTags incorporates both minimum_required_tags, and required_tag_groups
      if (this.model?.category?.minimumRequiredTags > tagsArray.length) {
        return EmberObject.create({
          failed: true,
          reason: i18n("composer.error.tags_missing", {
            count: this.model?.category?.minimumRequiredTags,
          }),
          lastShownAt: this.lastValidatedAt,
        });
      }
    }
  }

  collapse() {
    this._saveDraft();
    this.set("model.composeState", Composer.DRAFT);
    document.documentElement.style.setProperty("--composer-height", "40px");
  }

  toggleFullscreen() {
    this._saveDraft();

    const composer = this.model;

    if (composer?.viewFullscreen) {
      composer?.set("composeState", Composer.OPEN);
    } else {
      composer?.set("composeState", Composer.FULLSCREEN);
      composer?.set("showFullScreenExitPrompt", true);
    }
  }

  @computed("model.viewFullscreen", "model.showFullScreenExitPrompt")
  get showFullScreenPrompt() {
    return (
      this.model?.viewFullscreen &&
      this.model?.showFullScreenExitPrompt &&
      !this.capabilities.touch
    );
  }

  close() {
    // the 'fullscreen-composer' class is added to remove scrollbars from the
    // document while in fullscreen mode. If the composer is closed for any reason
    // this class should be removed

    const elem = document.documentElement;
    elem.classList.remove("fullscreen-composer");
    elem.classList.remove("composer-open");

    document.activeElement?.blur();
    document.documentElement.style.removeProperty("--composer-height");
    this.setProperties({ model: null, lastValidatedAt: null });

    // This is a temporary solution to reset the saved form template state while we don't store drafts
    this.set("formTemplateInitialValues", undefined);
  }

  @computed("model.action")
  get canEdit() {
    return this.model?.action === "edit" && this.currentUser.can_edit;
  }

  @computed("model.composeState")
  get visible() {
    return this.model?.composeState && this.model?.composeState !== "closed";
  }

  clearLastValidatedAt() {
    this.set("lastValidatedAt", null);
    this.appEvents.trigger("composer-service:last-validated-at-cleared");
  }

  _initialLocale(opts) {
    if (opts?.locale) {
      return opts.locale;
    }

    // inherit post locale when editing, not when replying
    if (opts?.post?.locale && opts.action === Composer.EDIT) {
      return opts.post.locale;
    }

    return "";
  }
}

// For compatibility with themes/plugins which use `modifyClass` as if this is a controller
// https://api.emberjs.com/ember/5.1/classes/Service/properties/mergedProperties?anchor=mergedProperties
ComposerService.prototype.mergedProperties = ["actions"];
